import { useMemo, useState } from "react";
import { useLanguage } from "@/components/language-provider";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { API_BASE_URL } from "@/lib/config";

export default function ResetPassword() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const invalidLink = !email || !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: t("ui.weak_password"), description: t("ui.use_at_least_8_characters"), variant: "destructive" });
      return;
    }
    if (password !== confirm) {
      toast({ title: t("ui.passwords_don_t_match"), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, newPassword: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not reset the password.");
      setDone(true);
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            {invalidLink ? <AlertTriangle className="w-6 h-6 text-destructive" /> : <ShieldCheck className="w-6 h-6 text-primary" />}
          </div>
          <CardTitle>
            {invalidLink ? "Invalid link" : done ? "Password updated" : "Set a new password"}
          </CardTitle>
          <CardDescription>
            {invalidLink
              ? "This reset link is incomplete or has already been used."
              : done
                ? "Your password has been changed. You can sign in now."
                : `Choose a new password for ${email}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invalidLink ? (
            <Link href="/forgot-password">
              <Button variant="outline" className="w-full" data-testid="button-request-new-link">{t("ui.request_a_new_link")}</Button>
            </Link>
          ) : done ? (
            <Button className="w-full" onClick={() => setLocation("/login")} data-testid="button-go-login">
              {t("ui.go_to_login")}
            </Button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("ui.new_password")}</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder={t("ui.at_least_8_characters")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="input-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t("ui.confirm_new_password")}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder={t("ui.repeat_the_password")}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="input-confirm-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="button-reset-password">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
